import React, { useState } from "react";
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu as MuiMenu,
  MenuItem,
} from "@mui/material";

import { Link, NavLink, useNavigate } from "react-router-dom";

import {
  Menu,
  LayoutDashboard,
  BadgeIndianRupee,
  PhoneCall,
  UserMinus,
  UserCircle,
  Users,
  Slack,
  ListTodo,
  Info,
  History,
} from "lucide-react";

import ProfileImg from "../../src/assets/user.png";
import Logo from "../../src/assets/just-talk-logo.png";

const DRAWER_WIDTH = 200;

export default function AdminSideNav() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleAvatarClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/admin/login");
  };

  // -------------------- FIXED NAVIGATION STRUCTURE --------------------
  const navigationItems = [
    {
      section: "Dashboard",
      items: [
        {
          text: "Overview",
          icon: <LayoutDashboard size={20} />,
          path: "/admin/overview",
        }
      ],
    },
    {
      section: "Customer",
      items: [
        {
          text: "Customer",
          icon: <ListTodo size={20} />,
          path: "/admin/all-leads",
        },
      ],
    },

    {
      section: "DID Management",
      items: [
        {
          text: "DIDs",
          icon: <PhoneCall size={20} />,
          path: "/admin/dids",
        },
      ],
    },
    // {
    //   section: "Revenue",
    //   items: [
    //     {
    //       text: "Revenue",
    //       icon: <BadgeIndianRupee size={20} />,
    //       path: "/admin/revenue",
    //     },
    //   ],
    // },
    {
      section: "User Management",
      items: [
        {
          text: "All Users",
          icon: <Users size={20} />,
          path: "/admin/users",
        },
      ],
    },
    {
      section: "Drop-Off Customers",
      items: [
        {
          text: "Drop-Off",
          icon: <UserMinus size={20} />,
          path: "/admin/drop-off",
        },
           {
          text: "Details",
          icon: <Info size={20} />,
          path: "/admin/details",
        },
        {
          text: "Audit History",
          icon: <History size={20} />,
          path: "/admin/history",
        },
      ],
    },
  ];

  // -------------------- DRAWER CONTENT --------------------
  const drawer = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "white",
        borderRight: "1px solid #ccc",
      }}
    >
      <Box
        sx={{
          borderBottom: "1px solid #ccc",
        }}
      >
        {/* Logo */}
        <Box sx={{ p: "15px 25px 10px 22px" }}>
          <img src={Logo} style={{ height: 40 }} alt="Logo" />
        </Box>

        {/* Admin Label */}
        <Box sx={{ px: 3, mb: 2.5 }}>
          <Button
            fullWidth
            sx={{
              bgcolor: "#fcfcfcff",
              borderRadius: 4,
              border: "1px solid #ddd",
              color: "#333",
              fontWeight: 600,
              "&:hover": { bgcolor: "#eee" },
            }}
          >
            ADMIN PANEL
          </Button>
        </Box>
      </Box>

      {/* Navigation Sections */}
      <List sx={{ px: 2, flex: 1 }}>
        {navigationItems.map((section, secIndex) => (
          <Box key={secIndex} sx={{ mb: 1 }}>
            {section.items.map((item, itemIndex) => (
              <ListItem key={itemIndex} disablePadding>
                <ListItemButton
                  component={NavLink}
                  to={item.path}
                  sx={{
                    borderRadius: 2,
                    "&.active": {
                      bgcolor: "#6366F1",
                      "& .MuiListItemText-primary": { color: "white" },
                      "& .MuiListItemIcon-root": { color: "white" },
                    },
                    // "&:hover": { bgcolor: "#f0f0f0" },
                  }}
                >
                  <ListItemIcon sx={{ color: "grey", minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>

                  <ListItemText
                    primary={item.text}
                    sx={{
                      "& .MuiListItemText-primary": {
                        color: "black",
                        fontSize: "0.875rem",
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </Box>
        ))}
      </List>

      {/* Add Footer */}
      <Box sx={{ p: 3 }}>
        <img
          src="https://material-kit-react.devias.io/assets/devias-kit-pro.png"
          style={{ width: "100%", borderRadius: 6 }}
          alt="pro"
        />
      </Box>
    </Box>
  );

  // -------------------- MAIN RETURN --------------------
  return (
    <Box>
      {/* Mobile Header */}
      {isMobile && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            p: "5px 20px",
            borderBottom: "1px solid #eee",
          }}
        >
          <IconButton onClick={handleDrawerToggle}>
            <Menu size={24} color="black" />
          </IconButton>

          <Avatar
            src={ProfileImg}
            sx={{ width: 29, height: 29, cursor: "pointer" }}
            onClick={handleAvatarClick}
          />
        </Box>
      )}

      {/* Avatar Menu */}
      <MuiMenu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
        <MenuItem onClick={() => navigate("/profile")}>Profile</MenuItem>
        <MenuItem onClick={handleLogout}>Logout</MenuItem>
      </MuiMenu>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            bgcolor: "white",
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            bgcolor: "white",
            border: "none",
          },
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
}
